import { useAuth0 } from "@auth0/auth0-react";
import {
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  Paper,
} from "@mui/material";
import PropTypes from "prop-types";
import { useState, useEffect } from "react";

import API_Calls_Dashboard from "@/api/calls/API_Calls_Dashboard";
import API_Calls_DataFile from "@/api/calls/API_Calls_DataFile";
import API_Calls_Preview from "@/api/calls/API_Calls_Preview";
import SubmittedFilesTableHeader from "@/components/membership/Dashboard/DashboardTables/SubmittedFilesTable/SubmittedFilesTableHeader";
import SubmittedFilesTableRow from "@/components/membership/Dashboard/DashboardTables/SubmittedFilesTable/SubmittedFilesTableRow";
import { filterRows } from "@/components/membership/Dashboard/DashboardUtils/DashboardUtil";
import Notifications from "@/components/Notifications/Notifications";
import { parseAdaPath, getComparator, stableSort } from "@/utils/util";

import "@/components/membership/Dashboard/Dashboard.css";

function SubmittedFilesTable({
  dashboardData,
  filter,
  searchTerm,
  startDate,
  endDate,
  selectedLocal,
}) {
  const { user } = useAuth0();
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("processDate");
  const [filteredRows, setFilteredRows] = useState([]); // this is the total rows after filter is applied
  const [visibleRows, setVisibleRows] = useState([]); // this is the total rows that appear in the table per page
  const [notification, setNotification] = useState({});
  const { downloadFile } = API_Calls_DataFile();
  const { downloadCatalistFile, deleteProcessedFile } = API_Calls_Dashboard();
  const { setFileAsIEB } = API_Calls_Preview();

  // when new filter or local is selected, reset current page to zero so user isn't shown a blank page and have to click back to the first page to see results
  useEffect(() => {
    setPage(0);
  }, [filter, selectedLocal]);

  useEffect(() => {
    setRows(createRowData(dashboardData.files));
    setVisibleRows(createRowData(dashboardData.files));
  }, [dashboardData]);

  useEffect(() => {
    createVisibleRows();
    setFilteredRows(rows);
    // createVisibleRows is an inline function recreated every render;
    // depending on it would cause infinite re-runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  useEffect(() => {
    createVisibleRows();
    // createVisibleRows is an inline function recreated every render;
    // intentionally react to the listed primitive deps only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filter,
    searchTerm,
    endDate,
    startDate,
    selectedLocal,
    order,
    orderBy,
    rowsPerPage,
    page,
  ]);

  const createVisibleRows = (_) => {
    const filteredData = filterRows(
      rows,
      searchTerm,
      filter,
      startDate,
      endDate,
      selectedLocal,
      dashboardData
    );

    setFilteredRows(filteredData);

    const visibleRows = stableSort(
      filteredData,
      getComparator(order, orderBy)
    ).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    setVisibleRows(visibleRows);
  };

  const createRowData = (files) => {
    const result = [];
    files.forEach((row) => {
      const parsedAdaPath = parseAdaPath(row.ada_file);
      row["fileName"] = parsedAdaPath.filename;
      row["uploadDate"] = parsedAdaPath.upload_date;
      row["local"] = parsedAdaPath.affiliate;
      row["processDate"] = parsedAdaPath.process_date;
      result.push(row);
    });
    return result;
  };

  const handleLocallyConfirmedFile = (file) => {
    const updatedRows = rows.map((row) =>
      row.ada_file === file.ada_file
        ? { ...row, confirmed: "Just confirmed" }
        : row
    );
    setRows(updatedRows);
  };

  const handleUnstagedFile = (file) => {
    const updatedRows = rows.map((row) =>
      row.ada_file === file.ada_file ? { ...row, VAN: "unstaged" } : row
    );
    setRows(updatedRows);
  };

  const handleDeleteFile = async (filename) => {
    const pf = parseAdaPath(filename);
    const ada_file = `${pf.process_date}/${pf.filename}@${pf.upload_date}/${pf.template_name}@${pf.template_date}/ada.parquet`;
    const response = await deleteProcessedFile(pf.affiliate, ada_file);
    if (!response.error) {
      // remove file from list o files
      setRows((prevRows) =>
        prevRows.filter((row) => row.ada_file !== filename)
      );
      setNotification({
        message: `File has been deleted.`,
        severity: "success",
        duration: 3000,
      });
    } else {
      setNotification({
        message: "Unable to delete this file",
        severity: "error",
        duration: 7000,
      });
    }
  };

  const handleIEBChange = async (affiliate, processTimestamp, isIEB) => {
    // set IEB value for this file
    setRows((prevRows) =>
      prevRows.map((row) => {
        if (row.processDate === processTimestamp && row.local === affiliate) {
          return { ...row, IEB_approved: isIEB };
        } else if (row.local === affiliate) {
          return { ...row, IEB_approved: false };
        }
        return row;
      })
    );

    // send processTimestamp for set or null for unset
    const value = isIEB ? processTimestamp : null;
    const res = await setFileAsIEB(affiliate, value);
    if (res.error) {
      // unset IEB value for this file
      setRows((prevRows) =>
        prevRows.map((row) => {
          if (row.processDate === processTimestamp && row.local === affiliate) {
            return { ...row, IEB_approved: false };
          }
          return row;
        })
      );
      setNotification({
        message: `Unable to set IEB file for ${affiliate} at this time`,
        severity: "error",
        duration: 7000,
      });
      console.error("Unable to set IEB: ", res.message);
    }
  };

  const handleDownloadAffiliateFile = async (file) => {
    showDownloadStartNotification();
    const parsedAdaFile = parseAdaPath(file.ada_file);
    const response = await downloadFile(
      parsedAdaFile.affiliate,
      file.fileName,
      file.uploadDate
    );
    if (response.error) {
      showDownloadFailedNotification();
    }
  };

  const handleDownloadCatalistFile = async (file) => {
    showDownloadStartNotification();
    const parsedAdaFile = parseAdaPath(file.ada_file);
    const response = await downloadCatalistFile(
      parsedAdaFile.affiliate,
      parsedAdaFile.process_date
    );
    if (response.error) {
      showDownloadFailedNotification();
    }
  };

  const showDownloadStartNotification = () => {
    setNotification({
      message: "Download started",
      severity: "info",
      duration: 3000,
    });
  };

  const showDownloadFailedNotification = () => {
    setNotification({
      message: "Unable to download at this time",
      severity: "error",
      duration: 7000,
    });
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const rowprops = {
    handleDownloadCatalistFile,
    handleDownloadAffiliateFile,
    handleDeleteFile,
    handleLocallyConfirmedFile,
    handleUnstagedFile,
    handleIEBChange,
    user,
  };

  return (
    <>
      <Paper sx={{ width: "100%", mb: 6 }}>
        <TableContainer>
          <Table
            sx={{ minWidth: 750 }}
            size={"medium"}
            data-testid="submitted-files-table"
          >
            <SubmittedFilesTableHeader
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              user={user}
            />
            <TableBody>
              {visibleRows.map((row, index) => (
                <SubmittedFilesTableRow
                  key={`table-row-${index}`}
                  {...rowprops}
                  row={row}
                  index={index}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      {!!Object.keys(notification).length && (
        <Notifications
          notification={notification}
          setNotification={setNotification}
        />
      )}
    </>
  );
}

SubmittedFilesTable.propTypes = {
  dashboardData: PropTypes.object,
  filter: PropTypes.string,
  searchTerm: PropTypes.string,
  startDate: PropTypes.object,
  endDate: PropTypes.object,
};

export default SubmittedFilesTable;
